import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="px-16 py-6">
          <div className="flex items-center">
            <img 
              src="https://public.readdy.ai/ai/img_res/a25660a9-5a21-40e1-9a9c-1d0233467b29.png" 
              alt="SARL MGB Logo" 
              className="h-12 w-auto mr-4"
            />
            <h1 className="text-2xl font-semibold text-gray-900">SARL MGB</h1>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h2 className="text-7xl font-serif leading-tight text-gray-900" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.02em' }}>
                Modern iOS<br />
                Applications<br />
                For Everyone
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
                Expert team developing cutting-edge applications for iOS platform, focusing on security and user experience enhancement.
              </p>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 rounded-3xl transform rotate-3"></div>
              <img
                src="https://public.readdy.ai/ai/img_res/c7f134d72b51c8246451de9da8b3ed69.jpg"
                alt="iOS Applications"
                className="relative z-10 w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-16 bg-stone-50">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <p className="text-xs uppercase tracking-widest text-gray-500 font-medium">WHO WE ARE</p>
          <h3 className="text-5xl font-serif text-gray-900 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Dedicated Team of<br />iOS Specialists
          </h3>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            SARL MGB is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices. We are committed to delivering innovative solutions that prioritize user privacy, device performance, and intuitive design. With years of experience in iOS development, our team combines technical excellence with a deep understanding of user needs to create applications that truly make a difference in daily mobile device usage.
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-4">OUR PRODUCTS</p>
            <h3 className="text-5xl font-serif text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Powerful iOS Solutions
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* VPN Product Card */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="h-96 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-8">
                <img
                  src="https://readdy.ai/api/search-image?query=iPhone%20displaying%20VPN%20application%20interface%20with%20glowing%20blue%20connection%20button%2C%20secure%20tunnel%20visualization%2C%20world%20map%20with%20server%20locations%2C%20shield%20icon%2C%20modern%20iOS%20design%20with%20gradient%20blue%20purple%20colors%2C%20clean%20professional%20app%20screenshot%2C%20high%20quality%20digital%20illustration&width=400&height=600&seq=vpn-product-002&orientation=portrait"
                  alt="VPN Solution"
                  className="w-64 h-auto drop-shadow-2xl"
                />
              </div>
              <div className="p-10 space-y-6">
                <h4 className="text-3xl font-bold text-gray-900">VPN Solution</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-shield-check-line text-indigo-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Secure Connection</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-global-line text-indigo-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Global Servers</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-lock-line text-indigo-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Privacy Protection</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="h-96 bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-8">
                <img
                  src="https://readdy.ai/api/search-image?query=iPhone%20showing%20device%20cleaner%20app%20interface%20with%20colorful%20storage%20analysis%20pie%20charts%2C%20file%20management%20icons%2C%20optimization%20progress%20bars%2C%20clean%20modern%20iOS%20design%20with%20green%20teal%20gradient%20colors%2C%20professional%20app%20screenshot%2C%20high%20quality%20digital%20illustration&width=400&height=600&seq=cleaner-product-003&orientation=portrait"
                  alt="Device Cleaner"
                  className="w-64 h-auto drop-shadow-2xl"
                />
              </div>
              <div className="p-10 space-y-6">
                <h4 className="text-3xl font-bold text-gray-900">Device Cleaner</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-database-2-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Storage Optimization</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-refresh-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Cache Management</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-speed-up-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Performance Boost</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-4">WHY CHOOSE US</p>
            <h3 className="text-5xl font-serif text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Excellence in Every Detail
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Security First */}
            <div className="text-center space-y-6 p-12">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <i className="ri-shield-star-line text-white text-3xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900">Security First</h4>
              <p className="text-gray-600 leading-relaxed">
                We prioritize the security of your data with industry-leading encryption and privacy protocols, ensuring your information remains protected at all times.
              </p>
            </div>

            {/* Intuitive Design */}
            <div className="text-center space-y-6 p-12">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                <i className="ri-palette-line text-white text-3xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900">Intuitive Design</h4>
              <p className="text-gray-600 leading-relaxed">
                Our applications feature user-centric interfaces that are both beautiful and easy to navigate, making complex tasks simple and enjoyable.
              </p>
            </div>

            {/* Optimized Performance */}
            <div className="text-center space-y-6 p-12">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-pink-500 to-orange-600 flex items-center justify-center">
                <i className="ri-rocket-line text-white text-3xl"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900">Optimized Performance</h4>
              <p className="text-gray-600 leading-relaxed">
                Built with cutting-edge technology and optimization techniques, our apps deliver lightning-fast performance without draining your battery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-16 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Company Info */}
            <div className="space-y-6">
              <h3 className="text-4xl font-serif text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                SARL MGB
              </h3>
              <p className="text-gray-400 text-lg">
                Enhancing iOS Experience Through Innovation
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-6">GET IN TOUCH</p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-purple-400 text-xl mt-1"></i>
                  <div>
                    <p className="text-gray-400 text-sm">Address</p>
                    <p className="text-white text-base">6 ALL HENRI SELLIER</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-building-line text-purple-400 text-xl mt-1"></i>
                  <div>
                    <p className="text-gray-400 text-sm">City</p>
                    <p className="text-white text-base">TOULOUSE</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-mail-line text-purple-400 text-xl mt-1"></i>
                  <div>
                    <p className="text-gray-400 text-sm">Zip</p>
                    <p className="text-white text-base">31400</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-global-line text-purple-400 text-xl mt-1"></i>
                  <div>
                    <p className="text-gray-400 text-sm">Country</p>
                    <p className="text-white text-base">France</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-mail-send-line text-purple-400 text-xl mt-1"></i>
                  <div>
                    <p className="text-gray-400 text-sm">Email</p>
                    <p className="text-white text-base">info@sarlmgb.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-phone-line text-purple-400 text-xl mt-1"></i>
                  <div>
                    <p className="text-gray-400 text-sm">Number</p>
                    <p className="text-white text-base">+1 8148512571</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © 2025 SARL MGB. All rights reserved.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 text-sm hover:text-purple-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}